import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { X, ChevronLeft, ChevronRight, ExternalLink, Calendar, Tag, ArrowRight, FolderOpen } from 'lucide-react';
import { API_BASE_URL } from '../config';

const SLIDE_INTERVAL = 3000;

/* ---------- Tarjeta de proyecto ---------- */
const ProjectCard = ({ project, onClick, viewLabel }) => {
  const [index, setIndex] = useState(0);
  const images = project.images?.length ? project.images : [];

  // Transicion automatica de imagenes cada 3 segundos
  useEffect(() => {
    if (images.length < 2) return undefined;
    const interval = setInterval(() => {
      setIndex((prev) => (prev + 1) % images.length);
    }, SLIDE_INTERVAL);
    return () => clearInterval(interval);
  }, [images.length]);

  return (
    <article
      onClick={onClick}
      data-testid={`portfolio-card-${project.id}`}
      className="group cursor-pointer"
    >
      <div className="relative aspect-[4/3] overflow-hidden rounded-xl bg-slate-900 shadow-md group-hover:shadow-2xl transition-shadow duration-500">
        {images.length === 0 && (
          <div className="absolute inset-0 flex items-center justify-center text-slate-600">
            <FolderOpen size={48} />
          </div>
        )}
        {images.map((img, i) => (
          <img
            key={i}
            src={img}
            alt={`${project.title} - ${i + 1}`}
            loading="lazy"
            className={`absolute inset-0 w-full h-full object-cover transition-all duration-1000 ease-out
              grayscale group-hover:grayscale-0 group-hover:scale-105
              ${i === index ? 'opacity-100 animate-ken-burns' : 'opacity-0'}`}
          />
        ))}

        {/* Indicador de imagenes */}
        {images.length > 1 && (
          <div className="absolute top-3 right-3 flex gap-1.5 z-10">
            {images.map((_, i) => (
              <span
                key={i}
                className={`h-1.5 rounded-full transition-all duration-500 ${i === index ? 'w-5 bg-white' : 'w-1.5 bg-white/50'}`}
              />
            ))}
          </div>
        )}

        {/* Overlay al pasar el cursor: la imagen se colorea y aparece el resumen completo */}
        <div className="absolute inset-0 bg-gradient-to-t from-slate-900/95 via-slate-900/50 to-transparent
          opacity-0 group-hover:opacity-100 transition-opacity duration-500
          flex flex-col justify-end p-6 z-10">
          <span className="text-primary-foreground/80 text-xs font-bold uppercase tracking-widest mb-2 flex items-center gap-2 translate-y-4 group-hover:translate-y-0 transition-transform duration-500">
            <Tag size={12} /> {project.category}
            {project.date && <>· <Calendar size={12} /> {project.date}</>}
          </span>
          <h3 className="text-white text-2xl font-bold leading-tight translate-y-4 group-hover:translate-y-0 transition-transform duration-500 delay-75">
            {project.title}
          </h3>
          <p className="text-slate-300 text-sm mt-2 line-clamp-3 translate-y-4 group-hover:translate-y-0 transition-transform duration-500 delay-100">
            {project.description}
          </p>
          <span className="inline-flex items-center gap-2 text-white font-semibold text-sm mt-4 translate-y-4 group-hover:translate-y-0 transition-transform duration-500 delay-150">
            {viewLabel} <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
          </span>
        </div>
      </div>

      {/* Pie de tarjeta */}
      <div className="pt-4 flex items-start justify-between gap-3">
        <div>
          <h3 className="font-bold text-lg leading-snug group-hover:text-primary transition-colors duration-300">
            {project.title}
          </h3>
          <p className="text-sm text-muted-foreground mt-1">
            {project.category}{project.date ? ` · ${project.date}` : ''}
          </p>
        </div>
      </div>
    </article>
  );
};

/* ---------- Ventana flotante con el detalle ---------- */
const ProjectModal = ({ project, onClose, visitLabel }) => {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const images = project.images || [];

  const next = useCallback(() => setIndex((prev) => (prev + 1) % images.length), [images.length]);
  const prev = useCallback(() => setIndex((prev) => (prev - 1 + images.length) % images.length), [images.length]);

  // Auto-avance cada 3s (se pausa al pasar el cursor) + teclado + bloqueo de scroll
  useEffect(() => {
    if (images.length < 2 || paused) return undefined;
    const interval = setInterval(next, SLIDE_INTERVAL);
    return () => clearInterval(interval);
  }, [next, images.length, paused]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') next();
      if (e.key === 'ArrowLeft') prev();
    };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [onClose, next, prev]);

  return (
    <div
      className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-in fade-in duration-300"
      onClick={onClose}
      data-testid="project-modal"
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto relative animate-in zoom-in-95 duration-300"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          data-testid="project-modal-close"
          className="absolute top-4 right-4 z-20 bg-black/50 hover:bg-black/70 text-white rounded-full p-2 transition-colors"
          aria-label="Cerrar"
        >
          <X size={20} />
        </button>

        {/* Galeria: las imagenes se ajustan al tamano del flotante */}
        {images.length > 0 && (
          <div
            className="relative bg-slate-900 rounded-t-2xl overflow-hidden"
            onMouseEnter={() => setPaused(true)}
            onMouseLeave={() => setPaused(false)}
          >
            <div className="h-[40vh] md:h-[50vh] flex items-center justify-center">
              {images.map((img, i) => (
                <img
                  key={i}
                  src={img}
                  alt={`${project.title} - ${i + 1}`}
                  className={`absolute inset-0 w-full h-full object-contain transition-opacity duration-700 ${i === index ? 'opacity-100' : 'opacity-0'}`}
                />
              ))}
            </div>

            {images.length > 1 && (
              <>
                <button
                  onClick={prev}
                  data-testid="modal-prev-image"
                  className="absolute left-3 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full p-2 transition-colors"
                  aria-label="Anterior"
                >
                  <ChevronLeft size={22} />
                </button>
                <button
                  onClick={next}
                  data-testid="modal-next-image"
                  className="absolute right-3 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full p-2 transition-colors"
                  aria-label="Siguiente"
                >
                  <ChevronRight size={22} />
                </button>
                <span className="absolute bottom-3 right-4 bg-black/60 text-white text-xs font-semibold px-2.5 py-1 rounded-full" data-testid="modal-image-counter">
                  {index + 1} / {images.length}
                </span>
              </>
            )}

            {/* Miniaturas */}
            {images.length > 1 && (
              <div className="absolute bottom-3 left-3 flex gap-2 max-w-[60%] overflow-x-auto">
                {images.map((img, i) => (
                  <button
                    key={i}
                    onClick={() => setIndex(i)}
                    data-testid={`modal-thumb-${i}`}
                    className={`flex-shrink-0 w-14 h-10 rounded overflow-hidden border-2 transition-all ${i === index ? 'border-white' : 'border-transparent opacity-60 hover:opacity-100'}`}
                  >
                    <img src={img} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Informacion detallada */}
        <div className="p-6 md:p-8">
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <span className="inline-flex items-center gap-1.5 bg-primary/10 text-primary text-xs font-bold px-3 py-1 rounded-full">
              <Tag size={12} /> {project.category}
            </span>
            {project.date && (
              <span className="inline-flex items-center gap-1.5 bg-slate-100 text-slate-600 text-xs font-semibold px-3 py-1 rounded-full">
                <Calendar size={12} /> {project.date}
              </span>
            )}
          </div>
          <h2 className="text-2xl md:text-3xl font-bold text-foreground" data-testid="modal-project-title">
            {project.title}
          </h2>
          <p className="mt-4 text-muted-foreground leading-relaxed whitespace-pre-line" data-testid="modal-project-description">
            {project.description}
          </p>
          {project.media_url && (
            <a
              href={project.media_url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="modal-project-link"
              className="inline-flex items-center gap-2 mt-6 bg-primary text-white font-semibold px-5 py-2.5 rounded-lg hover:bg-primary/90 transition-colors"
            >
              {visitLabel} <ExternalLink size={16} />
            </a>
          )}
        </div>
      </div>
    </div>
  );
};

/* ---------- Pagina Portfolio ---------- */
const PortfolioPage = () => {
  const { t } = useTranslation();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProject, setSelectedProject] = useState(null);
  const [activeCategory, setActiveCategory] = useState('');

  useEffect(() => {
    fetch(`${API_BASE_URL}/projects`)
      .then((res) => res.json())
      .then((data) => setProjects(Array.isArray(data) ? data : []))
      .catch((err) => console.error(err))
      .finally(() => setLoading(false));
  }, []);

  const categories = useMemo(
    () => [...new Set(projects.map((p) => p.category).filter(Boolean))].sort(),
    [projects]
  );

  const visible = useMemo(
    () => (activeCategory ? projects.filter((p) => p.category === activeCategory) : projects),
    [projects, activeCategory]
  );

  return (
    <div className="pt-32 min-h-screen bg-gray-50 pb-24" data-testid="portfolio-page">
      <div className="max-w-7xl mx-auto px-4">
        {/* Cabecera */}
        <div className="max-w-3xl mb-10">
          <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-4">{t('portfolio.title')}</h1>
          <p className="text-lg text-muted-foreground">{t('portfolio.subtitle')}</p>
        </div>

        {/* Filtros por tipo */}
        {categories.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-12" data-testid="portfolio-filters">
            <button
              onClick={() => setActiveCategory('')}
              data-testid="portfolio-filter-all"
              className={`px-4 py-2 text-xs font-bold uppercase tracking-widest border transition-all duration-300 ${
                activeCategory === ''
                  ? 'bg-foreground text-white border-foreground'
                  : 'bg-white text-foreground hover:border-foreground'
              }`}
            >
              {t('portfolio.filterAll')}
            </button>
            {categories.map((c) => (
              <button
                key={c}
                onClick={() => setActiveCategory(c)}
                data-testid={`portfolio-filter-${c}`}
                className={`px-4 py-2 text-xs font-bold uppercase tracking-widest border transition-all duration-300 ${
                  activeCategory === c
                    ? 'bg-foreground text-white border-foreground'
                    : 'bg-white text-foreground hover:border-foreground'
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        )}

        {/* Grid de 3 columnas */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[1, 2, 3].map((i) => (
              <div key={i} className="animate-pulse">
                <div className="aspect-[4/3] bg-slate-200 rounded-xl" />
                <div className="h-5 bg-slate-200 rounded mt-4 w-2/3" />
                <div className="h-4 bg-slate-100 rounded mt-2 w-1/3" />
              </div>
            ))}
          </div>
        ) : visible.length === 0 ? (
          <div className="text-center py-24 text-muted-foreground" data-testid="portfolio-empty">
            <FolderOpen size={48} className="mx-auto mb-4 text-slate-300" />
            <p>{t('portfolio.empty')}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="portfolio-grid">
            {visible.map((p) => (
              <ProjectCard
                key={p.id}
                project={p}
                viewLabel={t('portfolio.viewProject')}
                onClick={() => setSelectedProject(p)}
              />
            ))}
          </div>
        )}
      </div>

      {selectedProject && (
        <ProjectModal
          project={selectedProject}
          visitLabel={t('portfolio.visitWeb')}
          onClose={() => setSelectedProject(null)}
        />
      )}
    </div>
  );
};

export default PortfolioPage;
